import axios from 'axios';
import { BASE_URL } from './api';
import { supervisorModeParams } from '@/utils/outreachSupervisorMode';

// Conversations that have replies -- the replies inbox. One row per thread, not
// per draft: the same prospect answering three sends is one thing to deal with.
// Newest reply first, and scoped server-side to the mailboxes the user can send
// from, the same rule the draft queue uses.
export async function getOutreachThreads(params, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-threads/`,
        headers: { 'Authorization': `Token ${token}` },
        params: { ...params, ...supervisorModeParams() },
        // Same serializer as the draft queue: arrays become repeated params.
        paramsSerializer: (rawParams) => {
            const sp = new URLSearchParams()
            Object.entries(rawParams).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((item) => sp.append(key, item))
                } else if (value !== undefined && value !== null && value !== '') {
                    sp.append(key, value)
                }
            })
            return sp.toString()
        },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// One conversation, opened: the row's fields plus thread_messages,
// thread_drafts and reference_panel -- what the reading pane renders.
export async function getOutreachThread(threadId, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-threads/${threadId}/`,
        headers: { 'Authorization': `Token ${token}` },
        params: supervisorModeParams(),
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Start a template-free reply to one message on the thread. Returns the new
// draft. replyAll copies everyone else on that message.
export async function replyInOutreachThread(threadId, messageId, token, replyAll = false) {
    const data = { message_id: messageId }
    if (replyAll) data.reply_all = true
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-threads/${threadId}/reply/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data,
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Start a template follow-up to one message on the thread. templateId null
// reuses the message's template, else the last one the thread's sends used.
export async function prepareFollowUpInOutreachThread(threadId, messageId, token, templateId = null, replyAll = false) {
    const data = { message_id: messageId }
    if (templateId) data.template_id = templateId
    if (replyAll) data.reply_all = true
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-threads/${threadId}/prepare_follow_up/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data,
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Create a follow-up draft on each thread, replying to its most recent message.
// Threads that can't be followed up (bounced, already have a draft waiting, ...)
// come back in `skipped` with a reason rather than failing the batch.
export async function bulkPrepareFollowUpThreads(threadIds, token, templateId) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-threads/bulk_prepare_follow_up/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { thread_ids: threadIds, template_id: templateId },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Move threads to the user's Inbox, Archive or one of their own folders. Only
// for this user -- everyone else on a thread keeps it where they had it.
// `to` is 'inbox', 'archive' or a folder id. Threads the user can't see come
// back in `skipped`.
export async function moveOutreachThreads(threadIds, to, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-threads/move/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { thread_ids: threadIds, to },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// {inbox: n, archive: n, folders: {<id>: n}} over the threads the user can see,
// ignoring the list's filters.
export async function getOutreachThreadBoxCounts(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-threads/box-counts/`,
        headers: { 'Authorization': `Token ${token}` },
        params: supervisorModeParams(),
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Rule that threads' replies need no answer ('no_reply_needed'), that the
// prospect asked not to be contacted ('do_not_contact'), or clear the ruling
// (null). Shared by everyone on the thread, unlike folders and pins; a new reply
// brings "Needs reply" back on its own.
export async function setOutreachThreadReplyStatus(threadIds, replyStatus, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-threads/reply-status/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { thread_ids: threadIds, reply_status: replyStatus },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Pin threads to the top of wherever this user keeps them, or unpin them.
// Personal, like folders. Pinning an archived thread brings it back to the Inbox.
export async function pinOutreachThreads(threadIds, pinned, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-threads/pin/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { thread_ids: threadIds, pinned },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}
